import React from 'react';
import PhoneInput from 'react-phone-input-2';
import { ValidatorComponent } from 'react-material-ui-form-validator';
import { useTheme } from '@mui/material/styles';
import Label from '../Label';
import 'react-phone-input-2/lib/style.css';

// Theme-aware wrapper for PhoneInputField
const PhoneValidatorFieldWithTheme = React.forwardRef((props, ref) => {
    const theme = useTheme();

    // Inject CSS variables for theming
    React.useEffect(() => {
        const root = document.documentElement;
        root.style.setProperty('--primary-color', theme.palette.primary.main);
        root.style.setProperty('--border-color', theme.palette.textInput?.borderColor || theme.palette.primary.light);
    }, [theme]);

    return <PhoneInputField {...props} theme={theme} ref={ref} />;
});

class PhoneInputField extends ValidatorComponent {

    // Custom validation for Sri Lankan numbers
    validateSriLankanPhone = (value) => {
        if (!value) return true; // Let required validator handle empty values

        // Check if it's a Sri Lankan number (+94)
        if (value.startsWith('94')) {
            // Remove country code and check if remaining digits are exactly 9
            const localNumber = value.slice(2);
            // If only country code or incomplete, treat as empty (not invalid)
            if (localNumber.length === 0 || localNumber.length < 9) {
                return true; // Don't show error for incomplete numbers
            }
            return localNumber.length === 9 && /^\d{9}$/.test(localNumber);
        }

        return true; // For non-Sri Lankan numbers, use default validation
    };

    // Check if current value is valid
    isValidSriLankanNumber = () => {
        const { value, country } = this.props;
        if (country === 'lk' || (value && value.startsWith('94'))) {
            return this.validateSriLankanPhone(value);
        }
        return true;
    };

    // Override the validate method to handle phone validation
    validate = (value, includeRequired = false) => {
        const { validators = [], required } = this.props;

        // Check if field is required (either via prop or in validators array)
        const isRequired = required || (validators && validators.includes('required'));

        // If field is not required and value is empty or just country code, skip validation
        if (!isRequired && (!value || value === '94' || value === '+94')) {
            this.setState({ isValid: true });
            return Promise.resolve(true);
        }

        // For partial phone numbers (less than complete), skip validation if not required
        if (!isRequired && value && value.startsWith('94')) {
            const localNumber = value.slice(2);
            if (localNumber.length > 0 && localNumber.length < 9) {
                this.setState({ isValid: true });
                return Promise.resolve(true); // Allow typing without showing error
            }
        }

        // For all other cases, perform standard validation
        if (typeof super.validate === 'function') {
            return super.validate(value, includeRequired);
        }

        // Fallback if parent validate doesn't exist - just resolve as valid
        this.setState({ isValid: true });
        return Promise.resolve(true);
    };

    renderValidatorComponent() {
        const {
            value,
            onChange,
            disabled,
            placeholder,
            country,
            label,
            required,
            name,
            fullWidth = true,
            sx,
            theme,
            ...rest
        } = this.props;

        const customPhoneInputStyles = {
            width: fullWidth ? '100%' : 'auto',
            backgroundColor: theme?.palette?.background?.paper || '#fff',
            color: theme?.palette?.text?.primary || '#000',
            borderRadius: '5px',
            border: `1px solid ${theme?.palette?.textInput?.borderColor || '#e0e0e0'}`,
            boxShadow: `0px -0.04px 0px 2.7px ${theme?.palette?.textInput?.borderShadowColor || 'rgba(0,0,0,0.1)'}`,
            fontSize: '14px',
            paddingLeft: '58px', // Increased padding to accommodate flag dropdown
            paddingRight: '12px',
            paddingTop: '10px',
            paddingBottom: '10px',
            transition: 'all 0.3s ease',
            height: '40px', // Match TextValidator height
        };

        const buttonStyle = {
            backgroundColor: 'transparent',
            border: 'none',
            borderRight: `1px solid ${theme?.palette?.textInput?.borderColor || '#e0e0e0'}`,
            borderRadius: '3px 0 0 3px',
            height: '38px',
            width: '50px', // Fixed width for flag dropdown
        };

        const dropdownStyle = {
            backgroundColor: theme?.palette?.background?.paper || '#fff',
            border: `1px solid ${theme?.palette?.textInput?.borderColor || '#e0e0e0'}`,
            borderRadius: '5px',
            boxShadow: `0px 4px 8px ${theme?.palette?.textInput?.borderShadowColor || 'rgba(0,0,0,0.1)'}`,
        };

        const containerStyles = {
            width: fullWidth ? '100%' : 'auto',
            ...sx
        };

        return (
            <div style={containerStyles}>
                {label && (
                    <Label htmlFor={name} required={required}>{label}</Label>
                )}
                <PhoneInput
                    country={country || 'lk'}
                    value={value}
                    onChange={(phoneValue, countryData) => {
                        // Ensure the phone value always has the + prefix
                        const formattedValue = phoneValue && !phoneValue.startsWith('+')
                            ? `+${phoneValue}`
                            : phoneValue;
                        onChange(formattedValue, countryData);
                        // Force re-render to trigger validation display
                        this.forceUpdate();
                    }}
                    placeholder={placeholder}
                    disabled={disabled}
                    enableLongNumbers={true}
                    countryCodeEditable={false}
                    {...rest}
                    inputStyle={customPhoneInputStyles}
                    buttonStyle={buttonStyle}
                    dropdownStyle={dropdownStyle}
                    containerClass="phone-input-container"
                />
                {this.errorText()}
            </div>
        );
    }

    errorText() {
        const { isValid } = this.state;
        const { theme, value, country, required } = this.props;

        // First check standard validation
        if (!isValid) {
            return (
                <span style={{
                    color: theme?.palette?.error?.main || '#f44336',
                    fontSize: '12px',
                    marginTop: '4px',
                    display: 'block',
                    fontFamily: theme?.typography?.fontFamily || 'inherit',
                    fontWeight: 400,
                    lineHeight: 1.66
                }}>
                    {this.getErrorMessage()}
                </span>
            );
        }

        // Then check custom Sri Lankan validation
        // Only show error if field has value OR if it's required
        if ((country === 'lk' || (value && value.startsWith('94'))) && value && !this.isValidSriLankanNumber()) {
            return (
                <span style={{
                    color: theme?.palette?.error?.main || '#f44336',
                    fontSize: '12px',
                    marginTop: '4px',
                    display: 'block',
                    fontFamily: theme?.typography?.fontFamily || 'inherit',
                    fontWeight: 400,
                    lineHeight: 1.66
                }}>
                    Invalid phone number
                </span>
            );
        }

        return null;
    }
}

// Add custom CSS for phone input theming that matches TextValidator
const phoneInputCustomCSS = `
.phone-input-container .react-tel-input .form-control:focus {
    border-color: var(--primary-color, #6b5bc8) !important;
    border-width: 2px !important;
    box-shadow: 0px -0.2px 0px 1px var(--primary-color, #6b5bc8) !important;
    outline: none !important;
}

.phone-input-container .react-tel-input .form-control:hover:not(:focus) {
    border-color: var(--primary-color, #6b5bc8) !important;
}

.phone-input-container .react-tel-input .flag-dropdown:hover .selected-flag {
    background: transparent !important;
}

.phone-input-container .react-tel-input .flag-dropdown.open .selected-flag {
    background: transparent !important;
}

.phone-input-container .react-tel-input .flag-dropdown {
    border: none !important;
    border-right: 1px solid var(--border-color, #9482d7) !important;
    width: 50px !important;
    position: absolute !important;
    left: 0 !important;
    top: 0 !important;
    bottom: 0 !important;
}

.phone-input-container .react-tel-input .selected-flag {
    border-radius: 3px 0 0 3px !important;
    width: 50px !important;
    padding-left: 8px !important;
    padding-right: 8px !important;
}

.phone-input-container .react-tel-input .flag-dropdown.open {
    border-radius: 5px 0 0 0 !important;
}

.phone-input-container .react-tel-input .country-list {
    z-index: 9999 !important;
}

.phone-input-container .react-tel-input {
    position: relative !important;
}
`;

// Inject custom CSS
if (typeof document !== 'undefined') {
    const styleElement = document.createElement('style');
    styleElement.type = 'text/css';
    styleElement.innerHTML = phoneInputCustomCSS;
    if (!document.head.querySelector('style[data-phone-input-theme]')) {
        styleElement.setAttribute('data-phone-input-theme', 'true');
        document.head.appendChild(styleElement);
    }
}

export default PhoneValidatorFieldWithTheme;
